/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { createAction } from '@reduxjs/toolkit';
import { SetEntityPropertyParams } from 'app';

import { getCurrentStorageUserScopeId } from '~/app/storageScope';
import { HorseSelectionData } from '~/components/HorseSelection';
import { StateMachineValue } from '~/server/src/tmmodel';
import { persistDebugSpawnSource } from '~/setup/debugSpawnStorage';

import { Player } from './reducer';

export interface StartTrainingParameters {
  gameName?: string;
  playerCount: number;
  horseCount: number;
  horseSelection: HorseSelectionData;
  oppHorseSelection: HorseSelectionData;
  timeout: number;
  userId?: string;
}

/** Where a debug free-game spawn was initiated (controls premium bypass scope). */
export type DebugSpawnSource = 'snapshot' | 'common' | 'other';

export interface StartDebugParameters {
  gameName?: string;
  horseCount?: number;
  playerCount?: number;
  spawnSource?: DebugSpawnSource;
}

export interface StartFreeParameters {
  gameName?: string;
}

export const setConnected = createAction<boolean>('setConnected');
export const resetConnectionTimeout = createAction('resetConnectionTimeout');
export const notifyConnectionTimeout = createAction('notifyConnectionTimeout');

export const startTraining = createAction<StartTrainingParameters>('startTraining');
export const startFree = createAction('startFree');
export const startTournament = createAction('startTournament');
export const startDebug = createAction(
  'startDebug',
  (params?: StartDebugParameters) => {
    persistDebugSpawnSource(params?.spawnSource, getCurrentStorageUserScopeId());
    return { payload: params };
  },
);

/** Spectator: start watching a public game. Payload: { gameId, initialGameState } (GameState from GET /games/:gameId). */
export const startSpectator = createAction<{
  gameId: string;
  initialGameState: import('~/server/src/dtos').GameState;
}>('startSpectator');
/** Spectator: stop watching. Payload: { gameId }. */
export const stopSpectator = createAction<{ gameId: string }>('stopSpectator');
export type SpectatorStatus = 'idle' | 'connecting' | 'joined' | 'error';
export const setSpectatorStatus = createAction<SpectatorStatus>('setSpectatorStatus');

export const setCurrentState = createAction<StateMachineValue>('setCurrentState');
export const setRound = createAction<number>('setRound');

export const setTurnPos = createAction<number>('setTurnPos');
export const setTurnOrder = createAction<number[]>('setTurnOrder');
export const setTurnDices = createAction<number[]>('setTurnDices');
export const setTurnDicesCur = createAction<number>('setTurnDicesCur');
export const setTurnType = createAction<number>('setTurnType');
export const setTurnDicePlayerId = createAction<number>('setTurnDicePlayerId');

export const setPlayerId = createAction<number>('setPlayerId');
export const setPlayers = createAction<Player[]>('setPlayers');

export type HorseRanks = { rank: number; totalRank: number };
export const setHorsesRanks = createAction<HorseRanks[]>('setHorsesRanks');
export const setHorseRanks = createAction<SetEntityPropertyParams<HorseRanks>>('setHorseRanks');
export const setHorsesHandi = createAction<number[]>('setHorsesHandi');
export const setHorseHandi = createAction<SetEntityPropertyParams<number>>('setHorseHandi');
export const setHorsesPoints = createAction<number[][]>('setHorsesPoints');
export const setHorsePoints = createAction<SetEntityPropertyParams<number[]>>('setHorsePoints');
export const setHorsesFinished = createAction<boolean[]>('setHorsesFinished');
export const setHorseFinished = createAction<SetEntityPropertyParams<boolean>>('setHorseFinished');
export const setHorsesDropped = createAction<boolean[]>('setHorsesDropped');
export const setHorseDropped = createAction<SetEntityPropertyParams<boolean>>('setHorseDropped');
export const setHorsesStarted = createAction<boolean[]>('setHorsesStarted');
export const setHorsesBehindGoal = createAction<boolean[]>('setHorsesBehindGoal');

/** Trigger REJOINGAME flow to restore state when returning to live mode from history */
export const triggerRejoinGame = createAction('triggerRejoinGame');

export type LeaveLifecycleStatus = 'idle' | 'pending' | 'failed' | 'succeeded';
export type LeaveFailureReason = 'network' | 'controller' | 'auth' | 'unknown';
export const leaveRequested = createAction<{ attempt: number }>('leaveRequested');
export const leaveRetrying = createAction<{ attempt: number }>('leaveRetrying');
export const leaveAcked = createAction('leaveAcked');
export const leaveFailed = createAction<{ reason: LeaveFailureReason; message?: string }>(
  'leaveFailed',
);
export const leaveReset = createAction('leaveReset');
